import React, { ReactNode } from 'react';
import { connect } from 'react-redux';
import { FormControlLabel, TextField, InputAdornment, TextFieldVariants } from '@mui/material';
import { makeStyles } from '@mui/styles';
import HighlightMask from '@/components/HighlightMask/HighlightMask';
import AppTeamsButton from '@/components/UI/AppTeamsButton/AppTeamsButton';
import CloseButton from '@/components/UI/CloseButton/CloseButton';
import MandatoryLabel from '@/components/UI/MandatoryLabel/MandatoryLabel';
import { fieldTypes, NUMERIC_FIELDS } from '@/helpers/constants';
import { getFieldCursorType } from '@/helpers/utils';
import useCampaignSearchSelected from '@/hooks/useCampaignSearchSelected';
import { useFieldSelection } from '@/hooks/useFieldSelection';
import useWarningField from '@/hooks/useWarningField';
import { getCampaignSearch } from '@/redux/Campaign/campaign.reducer';
import { ThemeOptions } from '@/utils/componentStyles';
import TooltipComponent from '../Tooltip/TooltipComponent';
import styles from './InputText.module.scss';

const numbersFields = [
  NUMERIC_FIELDS.REQUEST_NO,
  NUMERIC_FIELDS.INPUT_NO,
  NUMERIC_FIELDS.INPUT_VEHICLE_VOLUME_NO,
  NUMERIC_FIELDS.SFM_REQUEST_NO,
];

interface InputTextInt {
  disabled?: boolean;
  id: string;
  label?: string | ReactNode;
  value: string;
  formControlClass?: string;
  handleChange?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  isMandatory?: boolean;
  variant?: TextFieldVariants;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
  onClick?: () => void;
  icoSearch?: boolean;
  urlTeams?: string;
  onBlur?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  invalid?: boolean;
  autoComplete?: string;
  clear?: () => void;
  type?: string;
  size?: any;
  helperText?: string[];
  className?: string;
  maxRows?: number;
  placeholder?: string;
  icoTeams?: boolean;
  fieldPath?: string[];
  userFieldPath?: string[];
  otherConditionInvalid?: { and: boolean; condition: boolean };
  startAdornment?: ReactNode;
  inputLabelClasses?: { [key: string]: string };
  isUserInput?: boolean;
  isPlantInput?: boolean;
  noTranslationField?: boolean;
  isCampaignSearchPopUpInput?: boolean;
  tooltipTitle?: string;
}

const InputText: React.FC<InputTextInt> = (props) => {
  const { disabled = false, id, label, formControlClass, handleChange = () => { }, isMandatory = false, variant = 'outlined',
    value = '', maxLength, multiline, rows, onClick = () => null, icoSearch, urlTeams, onBlur = () => null,
    invalid, autoComplete, clear = false, type, size, helperText = [], className, maxRows, placeholder, icoTeams, fieldPath = [],
    userFieldPath = [], otherConditionInvalid, startAdornment, inputLabelClasses = {}, isUserInput = false, isPlantInput = false,
    noTranslationField = false, isCampaignSearchPopUpInput = false, tooltipTitle,
  } = props;

  const getFieldType = (idd: string) => {
    if (numbersFields.includes(idd) || isPlantInput || type === 'number') return 'number';
    if (type === 'time') return 'time';

    return 'text';
  };

  const inputLabel = isMandatory ? (<>{label} <MandatoryLabel /></>) : label;
  const hasLabel = !(label === undefined || label === null || label === '');
  const { termNotAvailableInLanguage } = useCampaignSearchSelected(isUserInput
    ? userFieldPath
    : fieldPath, isUserInput ? fieldTypes.INPUT_USER : getFieldType(id), noTranslationField);

  const [tab, fieldName, fieldSecondaryName = ''] = isUserInput ? userFieldPath : fieldPath;
  const { handleFieldSelection, isFieldSelected, isSelectionModeOn } = useFieldSelection({ tab, fieldName, fieldSecondaryName });

  const warning = useWarningField(fieldPath, getFieldType(id), otherConditionInvalid) || invalid || isFieldSelected;

  const handleClear = (ev: React.MouseEvent<HTMLButtonElement>) => {
    clear && clear();
    ev.stopPropagation();
  };

  const useStyles = makeStyles(({ cartTheme }: ThemeOptions) => ({
    input: {
      '& input::placeholder': {
        fontSize: '10px',
      },
      placeholderFont: {
        '& .MuiInputBase-root': warning
          ? {
            backgroundColor: cartTheme.alertWarningBg,
          }
          : {},
      },
    },
    selectedField: {
      '& .MuiInputBase-root': isFieldSelected
        ? {
          backgroundColor: cartTheme.alertWarningBg,
        }
        : {},
    },
    selectionModePointer: {
      '& *': getFieldCursorType(true),
    },
  }));

  const helperTextStyles = makeStyles(({ cartTheme }: ThemeOptions) => ({
    root: {
      '&$error': {
        color: cartTheme.infoMessageColor,
        fontSize: '12px',
        fontStyle: 'italic',
      },
    },
    error: {},
  }));

  const getStartAdornment = () => ((icoTeams)
    ? (
      <InputAdornment position="start">
        <AppTeamsButton teamsUrl={urlTeams || ''} />
      </InputAdornment>
    )
    : startAdornment
      ? (
        <InputAdornment position="start">{startAdornment}</InputAdornment>
      )
      : null);

  const classes: any = useStyles();
  const helperTestClasses: any = helperTextStyles();

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isFieldSelected) {
      handleFieldSelection(e);
    }

    handleChange(e);
  };

  const input = (
    <FormControlLabel
      className={styles.FormLabel}
      label={null}
      control={(
        <HighlightMask
          fieldId={id}
          fieldPath={isUserInput ? userFieldPath : fieldPath}
          value={value}
          isUserInput={isUserInput}
          fieldType={isUserInput ? fieldTypes.INPUT_USER : getFieldType(id)}
          disabled={disabled}
          isCampaignSearchPopUpInput={isCampaignSearchPopUpInput}
          noTranslationField
        >
          <TextField
            className={`${classes.input} ${isSelectionModeOn ? classes.selectionModePointer : ''}`}
            classes={{ root: `${classes.placeholderFont} ${classes.selectedField}` }}
            variant={variant}
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#000000',
              },
            }}
            InputProps={{
              type: getFieldType(id),
              startAdornment: getStartAdornment(),
              endAdornment: !disabled && (clear || icoSearch) && (
                <>
                  <InputAdornment position="end">
                    {clear && (
                      <CloseButton onClick={handleClear} closeStyle={styles.IconClear} color="inherit" size="small" />
                    )}
                  </InputAdornment>
                  {icoSearch && (
                    <InputAdornment position="end">
                      <span>{icoSearch}</span><i className={`di icon-lupe-suche ${styles.IconSearch}`} />
                    </InputAdornment>
                  )}
                </>
              ),
            }}

            inputProps={{
              'data-testid': id,
              style: isSelectionModeOn ? { pointerEvents: 'none' } : {},
            }}
            FormHelperTextProps={{ classes: helperTestClasses }}
            InputLabelProps={{ ...(hasLabel ? { shrink: true } : {}), classes: inputLabelClasses }}
            error={warning || helperText.length > 0}
            helperText={!disabled && helperText.map((textError) => (<>{textError}<br /></>))}
            multiline={multiline}
            minRows={rows}
            maxRows={maxRows}
            disabled={disabled}
            id={id}
            {...(type ? { type } : {})}
            {...(autoComplete ? { autoComplete } : {})}
            {...(placeholder ? { placeholder } : {})}
            {...(hasLabel
              ? {
                label: <span className={termNotAvailableInLanguage ? styles.TermNotAvailableInLanguaje : ''}>{inputLabel}</span>,
              }
              : {})}
            onChange={handleOnChange}
            onBlur={onBlur}
            onClick={!disabled ? onClick : undefined}
            onInput={(e: any) => {
              e.target.value = maxLength ? e.target.value?.toString().slice(0, maxLength) : e.target.value;
            }}
            value={value}
            fullWidth
            size={size}
          />
        </HighlightMask>
      )}
    />
  );

  return (
    <>
      {tooltipTitle
        ? (
          <TooltipComponent title={tooltipTitle}>
            <span
              className={`${styles.FormLabelWrapper} ${formControlClass} ${className} ${warning ? 'ValidationIndicatorField' : ''}`}
              onClick={isSelectionModeOn ? handleFieldSelection : undefined}
            >
              {input}
            </span>
          </TooltipComponent>
        )
        : (
          <span
            className={`${styles.FormLabelWrapper} ${formControlClass} ${className} ${warning ? 'ValidationIndicatorField' : ''}`}
            onClick={isSelectionModeOn ? handleFieldSelection : undefined}
          >
            {input}
          </span>
        )}
    </>
  );
};

const mapStateToProps = (state) => ({
  campaignSearch: getCampaignSearch(state),
});

export default connect(mapStateToProps)(InputText);
